import { z } from 'zod';

import {
  CashDenominationConfidenceSchema,
  CashDenominationSourceSchema,
} from './cash-denominations.schema.js';
import { supportedCashDenominationMinorValues } from './cash-denomination-snapshots.dto.js';
import { CashLocationTypeSchema } from './cash-locations.schema.js';
import { CashMovementDirectionSchema, CashMovementKindSchema } from './cash-movement.schema.js';
import {
  cashDenominationUsageCategoryValues,
  type CashDenominationUsageCategory,
  type CashDenominationUsageCoverageRowDto,
  type CashDenominationUsageDenominationRowDto,
  type CashDenominationUsageMovementRowDto,
  type CashDenominationUsageSummaryRowDto,
  type GetCashDenominationUsageReportRequest,
  type GetCashDenominationUsageReportResponse,
} from './cash-report-denomination-usage.dto.js';
import {
  CashReportActorRefDtoSchema,
  CashReportCashLocationRefDtoSchema,
  CashReportMovementCategorySchema,
  CashReportPurchasePayoutRefDtoSchema,
  CashReportSessionRefDtoSchema,
  CashReportSourceRefDtoSchema,
  CashReportVarianceActionRefDtoSchema,
  CashReportVarianceCaseRefDtoSchema,
  cashReportTimestampSchema,
  validateCashReportDateRange,
} from './cash-report-common.schema.js';

const supportedDenominations = new Set<number>(supportedCashDenominationMinorValues);

export const CashDenominationUsageCategorySchema: z.ZodType<CashDenominationUsageCategory> = z.enum(
  cashDenominationUsageCategoryValues,
);

export const CashDenominationUsageDenominationRowDtoSchema: z.ZodType<CashDenominationUsageDenominationRowDto> =
  z
    .object({
      denominationMinor: z
        .number()
        .int()
        .positive()
        .refine((value) => supportedDenominations.has(value), 'Unsupported denomination'),
      count: z.number().int().positive(),
      amountMinor: z.number().int().positive(),
    })
    .superRefine((value, context) => {
      if (value.amountMinor !== value.denominationMinor * value.count) {
        context.addIssue({
          code: 'custom',
          message: 'amountMinor must equal denominationMinor multiplied by count.',
          path: ['amountMinor'],
        });
      }
    });

export const GetCashDenominationUsageReportRequestSchema: z.ZodType<GetCashDenominationUsageReportRequest> =
  z
    .object({
      companyId: z.uuid(),
      from: cashReportTimestampSchema,
      to: cashReportTimestampSchema,
      storeId: z.uuid().optional(),
      locationId: z.uuid().optional(),
      stationId: z.uuid().optional(),
      cashLocationId: z.uuid().optional(),
      cashLocationKind: CashLocationTypeSchema.optional(),
      usageCategory: CashDenominationUsageCategorySchema.optional(),
      movementKind: CashMovementKindSchema.optional(),
      reportCategory: CashReportMovementCategorySchema.optional(),
      source: CashDenominationSourceSchema.optional(),
      confidence: CashDenominationConfidenceSchema.optional(),
      denominationMinor: z
        .number()
        .int()
        .positive()
        .refine((value) => supportedDenominations.has(value), 'Unsupported denomination')
        .optional(),
    })
    .superRefine(validateCashReportDateRange);

export const CashDenominationUsageSummaryRowDtoSchema: z.ZodType<CashDenominationUsageSummaryRowDto> =
  z
    .object({
      denominationMinor: z
        .number()
        .int()
        .positive()
        .refine((value) => supportedDenominations.has(value), 'Unsupported denomination'),
      usageCategory: CashDenominationUsageCategorySchema,
      movementKind: CashMovementKindSchema,
      reportCategory: CashReportMovementCategorySchema,
      cashLocation: CashReportCashLocationRefDtoSchema,
      movementCount: z.number().int().nonnegative(),
      allocatedMovementCount: z.number().int().nonnegative(),
      grossInCount: z.number().int().nonnegative(),
      grossOutCount: z.number().int().nonnegative(),
      netCount: z.number().int(),
      grossInAmountMinor: z.number().int().nonnegative(),
      grossOutAmountMinor: z.number().int().nonnegative(),
      netDepletionAmountMinor: z.number().int(),
    })
    .superRefine((value, context) => {
      if (value.netCount !== value.grossOutCount - value.grossInCount) {
        context.addIssue({
          code: 'custom',
          message: 'netCount must equal grossOutCount minus grossInCount.',
          path: ['netCount'],
        });
      }
      if (value.grossInAmountMinor !== value.denominationMinor * value.grossInCount) {
        context.addIssue({
          code: 'custom',
          message: 'grossInAmountMinor must match denomination count.',
          path: ['grossInAmountMinor'],
        });
      }
      if (value.grossOutAmountMinor !== value.denominationMinor * value.grossOutCount) {
        context.addIssue({
          code: 'custom',
          message: 'grossOutAmountMinor must match denomination count.',
          path: ['grossOutAmountMinor'],
        });
      }
      if (value.netDepletionAmountMinor !== value.grossOutAmountMinor - value.grossInAmountMinor) {
        context.addIssue({
          code: 'custom',
          message: 'netDepletionAmountMinor must equal gross out minus gross in.',
          path: ['netDepletionAmountMinor'],
        });
      }
    });

export const CashDenominationUsageCoverageRowDtoSchema: z.ZodType<CashDenominationUsageCoverageRowDto> =
  z.object({
    usageCategory: CashDenominationUsageCategorySchema,
    movementKind: CashMovementKindSchema,
    reportCategory: CashReportMovementCategorySchema,
    cashLocation: CashReportCashLocationRefDtoSchema,
    totalMovementCount: z.number().int().nonnegative(),
    allocatedMovementCount: z.number().int().nonnegative(),
    amountOnlyMovementCount: z.number().int().nonnegative(),
    missingRowsMovementCount: z.number().int().nonnegative(),
    unsupportedMovementCount: z.number().int().nonnegative(),
    totalAmountMinor: z.number().int().nonnegative(),
    allocatedAmountMinor: z.number().int().nonnegative(),
    amountOnlyAmountMinor: z.number().int().nonnegative(),
    missingRowsAmountMinor: z.number().int().nonnegative(),
    unsupportedAmountMinor: z.number().int().nonnegative(),
  });

export const CashDenominationUsageMovementRowDtoSchema: z.ZodType<CashDenominationUsageMovementRowDto> =
  z.object({
    movementId: z.uuid(),
    companyId: z.uuid(),
    occurredAt: cashReportTimestampSchema,
    direction: CashMovementDirectionSchema,
    amountMinor: z.number().int().nonnegative(),
    movementKind: CashMovementKindSchema,
    reportCategory: CashReportMovementCategorySchema,
    usageCategory: CashDenominationUsageCategorySchema,
    isReversal: z.boolean(),
    cashLocation: CashReportCashLocationRefDtoSchema,
    session: CashReportSessionRefDtoSchema.nullable(),
    varianceCase: CashReportVarianceCaseRefDtoSchema.nullable(),
    varianceAction: CashReportVarianceActionRefDtoSchema.nullable(),
    purchasePayout: CashReportPurchasePayoutRefDtoSchema.nullable(),
    source: CashReportSourceRefDtoSchema.nullable(),
    actor: CashReportActorRefDtoSchema.nullable(),
    allocationId: z.uuid().nullable(),
    currentRevisionId: z.uuid().nullable(),
    allocationSource: CashDenominationSourceSchema.nullable(),
    allocationConfidence: CashDenominationConfidenceSchema.nullable(),
    denominationRows: z.array(CashDenominationUsageDenominationRowDtoSchema),
  });

export const GetCashDenominationUsageReportResponseSchema: z.ZodType<GetCashDenominationUsageReportResponse> =
  z.object({
    companyId: z.uuid(),
    from: cashReportTimestampSchema,
    to: cashReportTimestampSchema,
    summaryRows: z.array(CashDenominationUsageSummaryRowDtoSchema),
    coverageRows: z.array(CashDenominationUsageCoverageRowDtoSchema),
    movementRows: z.array(CashDenominationUsageMovementRowDtoSchema),
  });
